import React, { useEffect, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Cascader, Tooltip, Modal, Empty, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCheck, faList, faStar } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetFavoriteGridQuery,
  useLazyGetOrganizationsCascaderQuery,
  useLazyGetFavoriteGridsByOrganizationQuery,
} from "../../../redux/monitoring/monitoringAPI";
import { usePutUserMutation } from "../../../redux/users/userAPI";
import { resolveConfiguredMode } from "../../../utils/actionFiringMode";
import {
  updateCascaderSelectMultipleSelectionOptions,
  updateTopViewLayout,
  updateDetailViewLayout,
  updateStatusViewLayout,
  updateServiceViewLayout,
  updateIdGridSelected,
  updateFavoriteGridName,
  updateClearCascader,
  updateActionFiringMode,
  updateDoorUnknownAsOpening,
  updateHideUnconfiguredDoors,
} from "../../../redux/monitoring/monitoringSlice";
import { updateOrganizationSelectMultipleData } from "../../../redux/organizations/organizationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import {
  updateFloorsEnabled,
  updateNamesEnabled,
  updateServiceModeEnabled,
  updateShowOriginal,
} from "../../../redux/mapView/mapViewSlice";
import { formatUuidT } from "../../../flatbuffers/display";
// INTERFACES
import { RootState } from "../../../app/store";
import { ZERO_UUID_STRING } from "../../../utils/uuid";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
import { updateUserLogged } from "../../../redux/home/homeSlice";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

const CASCADER_STORAGE_KEY = "lnMonitoringCascaderSelection";

/**
 * `showSavedGrids`: the Saved Grids picker (icon + modal) is the Monitoring
 * Dashboard *Grids* block's, not the Banks block's — the cascader itself only
 * needs Banks. Defaults to true so other callers keep the picker.
 */
function OrganizationCascaderSelectMultiple({
  showSavedGrids = true,
}: {
  showSavedGrids?: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { SHOW_CHILD } = Cascader;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [cascaderData, setCascaderData] = useState([]);
  const [cascaderValue, setCascaderValue] = useState<string[]>([]);
  const [listFavoriteGrids, setListFavoriteGrids] = useState<any>([]);
  const [isGridModalOpen, setIsGridModalOpen] = useState(false);

  // ************************************************ */
  // SCOPE FILTERING ******************************* */
  const bankScopeAllows = useScopePredicate(PermissionAction.VIEW);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { cascaderSelectMultipleSelectionOptions, refreshPage, clearCascader, idGridSelected } =
    useSelector((state: RootState) => state.monitoring);
  const { theme, userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetOrganizationsCascader,
    { data: dataGetOrganizationsCascader, isLoading: isLoadingOrganizationCascader },
  ] = useLazyGetOrganizationsCascaderQuery();

  const [triggerGetGridById, { data: dataGridById, isLoading: isLoadingGridById }] =
    useLazyGetFavoriteGridQuery();

  const [
    triggerGetFavoriteGridsByOrganization,
    { data: dataGetFavoriteGrids, isLoading: isLoadingFavoriteGrids },
  ] = useLazyGetFavoriteGridsByOrganizationQuery();
  const [putUser] = usePutUserMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */

  /**
   * Returns the cascaderData filtered to what the current user is allowed to
   * see. Master admins see the entire tree; everyone else sees only their own
   * org.
   *
   * "Master admin" was historically detected by comparing the user's
   * organizationId against process.env.REACT_APP_MASTER_ORG_ID — which is
   * baked into the SPA at build time. That works on cloud deployments where
   * the master org UUID is known when the image is built, but breaks on
   * offline / field-bootstrapped installs (the org is created at runtime
   * with a different UUID, so the env-var check never matches and the
   * dropdown collapses to just the user's own org). The check now reads the
   * role name from the JWT-derived userLogged record — that mirrors the
   * backend's utils.IsMasterAdmin and always reflects the actual role
   * assigned in the database, regardless of when the org was provisioned.
   *
   * The env-var equality is preserved as a secondary OR-branch so existing
   * cloud deployments that depend on it keep working.
   */
  const getVisibleCascaderData = (data: any[]): any[] =>
    // `/monitor-view/` is already scoped server-side to the caller's org
    // subtree — own org + descendants for a tenant, the whole tree for a master
    // admin (see LiftCloud monitor-view.controller FindOrganizationTree →
    // GetOrgTreeIDs). No extra client-side org filter is needed: returning the
    // full response lets a parent-org user see their sub-orgs' banks, while a
    // single-org tenant still only has its own node. (Previously this dropped
    // every node except globalOrganizationId for non-master users, which hid
    // legitimately-visible sub-org banks from a parent-org user.)
    data;

  /** Persist current selection to localStorage */
  const persistSelection = (banks: any[], paths: any[]) => {
    if (!userLogged || !userLogged.id) return;
    // Cascader option payloads can contain FlatBuffer-decoded BigInt fields
    // (e.g. u64 channel ids). JSON.stringify throws on raw BigInts, so coerce
    // them to decimal strings via a replacer. The reload path doesn't need
    // them as actual BigInts since they're only used as cache keys/labels.
    localStorage.setItem(
      CASCADER_STORAGE_KEY,
      JSON.stringify(
        {
          userId: userLogged.id,
          organizationSelectMultipleData: banks,
          cascaderSelectMultipleSelectionOptions: paths,
        },
        (_key, value) => (typeof value === "bigint" ? value.toString() : value),
      ),
    );
  };

  /**
   * Build a `bankId → live cascader entry` map.
   *
   * The cascader entries are constructed in `buildCascaderArray` directly
   * from the most recent `/monitor-view/` response, so each entry carries
   * the current value of every bank field — including `visible`. Anything
   * downstream that needs to honor a CURRENT visibility flag (instead of
   * the snapshot persisted at selection time) should look the bank up here
   * rather than trusting the stored localStorage / favorite-grid payload.
   *
   * Why this exists: a bank persisted at `visible: true` and later toggled
   * off in the wizard would still flow through `bank.visible !== false`
   * (and therefore still render on the monitoring dashboard) because the
   * `organizationSelectMultipleData` Redux state carries the OLD bank
   * shape. Rehydrating from this map fixes that mismatch.
   */
  const collectLiveBanks = (data: any[]): Map<string, any> => {
    const liveBanks = new Map<string, any>();
    data.forEach((org: any) => {
      if (org.children) {
        org.children.forEach((serv: any) => {
          if (serv.children) {
            serv.children.forEach((chan: any) => {
              if (chan.children) {
                chan.children.forEach((bank: any) => {
                  liveBanks.set(bank.value, bank);
                });
              }
            });
          }
        });
      }
    });
    return liveBanks;
  };

  const initData = async () => {
    const token = await GETJwtToken();
    triggerGetOrganizationsCascader({ token });
    triggerGetFavoriteGridsByOrganization({
      token,
      idOrganization: userLogged.organizationId,
    });
    if (
      cascaderSelectMultipleSelectionOptions &&
      cascaderSelectMultipleSelectionOptions.length > 0
    ) {
      setCascaderValue(cascaderSelectMultipleSelectionOptions);
    }
  };

  const buildCascaderArray = () => {
    if (
      dataGetOrganizationsCascader &&
      dataGetOrganizationsCascader.data &&
      dataGetOrganizationsCascader.data.length > 0
    ) {
      const newArr: any = [];
      // ***************************************
      // BUILD ORGANIZATIONS CASCADER
      dataGetOrganizationsCascader.data.map((org: any) => {
        const orgCascader: any = {
          value: org.id,
          label: org.name,
          children: [],
          type: "organization",
        };
        // ***************************************
        // BUILD SERVER CASCADER
        if (org && org.servers && org.servers.length > 0) {
          org.servers.map((serv: any) => {
            if (serv && serv.active !== false) {
              const servCascader: any = {
                value: serv.id,
                label: serv.name,
                children: [],
                type: "server",
              };
              // ***************************************
              // BUILD CHANNELS CASCADER
              if (serv && serv.channels && serv.channels.length > 0) {
                serv.channels.map((channel: any) => {
                  const channelCascader: any = {
                    value: channel.id,
                    label: channel.name,
                    children: [],
                    type: "channel",
                  };
                  // ***************************************
                  // BUILD BANKS CASCADER
                  // Pre-filter banks flagged as not visible — they shouldn't
                  // appear in the dashboard cascader at all, mirroring the
                  // MapViewCascader behavior. Treat undefined/null as visible
                  // so legacy data (pre-`visible` field) keeps rendering.
                  if (channel && channel.banks && channel.banks.length > 0) {
                    channel.banks
                      .filter(
                        (bank: any) =>
                          bank.visible !== false && bankScopeAllows({ bankId: bank.id }),
                      )
                      .map((bank: any) => {
                        const bankCascader: any = {
                          value: bank.id,
                          label: bank.name,
                          children: [],
                          type: "bank",
                          idOrganization: org.id,
                          idServer: serv.id,
                          idChannel: channel.id,
                          idBank: bank.id,
                          nameOrganization: org.name,
                          nameServer: serv.name,
                          fbEnabled: serv.fb_enabled,
                          nameChannel: channel.name,
                          typeChannel: channel.type,
                          socketUrl: serv.socket_url,

                          ...bank,
                        };
                        return channelCascader.children.push(bankCascader);
                      });
                  }
                  return servCascader.children.push(channelCascader);
                });
              }
              return orgCascader.children.push(servCascader);
            }
            return true;
          });
        }
        return newArr.push(orgCascader);
      });
      setCascaderData(newArr);
      // Seed door-unknown toggle from the logged-in user's org
      if (userLogged && userLogged.organizationId) {
        const userOrg = dataGetOrganizationsCascader.data.find(
          (org: any) => org.id === userLogged.organizationId,
        );
        if (userOrg) {
          dispatch(updateDoorUnknownAsOpening(userOrg.door_unknown_as_opening || false));
          dispatch(updateHideUnconfiguredDoors(userOrg.hide_unconfigured_doors !== false));
          // The CONFIGURED value, not a behaviour: INHERIT means nobody chose,
          // and each surface then keeps its own default. Resolving to a
          // behaviour here would force one default on both surfaces.
          dispatch(
            updateActionFiringMode(
              resolveConfiguredMode(
                (userLogged as any)?.actionFiringMode ?? (userLogged as any)?.action_firing_mode,
                userOrg.action_firing_mode,
              ),
            ),
          );
        }
      }
    }
  };

  const onChangeCascader = (_: any, selectedOptions: any) => {
    const newArrSelectedOptions: any = [];
    const newArrSelectedBanks: any = [];
    if (selectedOptions) {
      selectedOptions.map((item: any) => {
        // Build cascader value array
        const subArr: any = [];
        item.map((item2: any) => subArr.push(item2.value));
        newArrSelectedOptions.push(subArr);
        // Get all banks selected
        if (item && item.length === 4) {
          newArrSelectedBanks.push(item[3]);
        }
        return true;
      });
    }
    dispatch(updateOrganizationSelectMultipleData(newArrSelectedBanks));
    dispatch(updateCascaderSelectMultipleSelectionOptions(newArrSelectedOptions));
    setCascaderValue(newArrSelectedOptions);
    persistSelection(newArrSelectedBanks, newArrSelectedOptions);
  };

  /**
   * Filter grid data against the user's visible org for non-master users.
   * Mirrors getVisibleCascaderData's master-admin detection: role name first
   * (works on offline/field installs where the master org UUID is created at
   * runtime), env-var equality as a fallback for legacy cloud deployments.
   */
  const filterGridByOrg = (jsonObject: any) => {
    // Pull the live bank lookup once — both branches below need to
    // rehydrate selected banks against the freshest `/monitor-view/`
    // response so a later wizard toggle on `visible`/`active` is
    // honored. The legacy code passed the master-admin payload
    // through untouched, which preserved whatever shape the bank had
    // when the grid was originally saved.
    const liveBanks = collectLiveBanks(cascaderData);

    // Predicate shared by both branches: a path's bank must (a) exist
    // in the freshly fetched cascader and (b) not have been hidden
    // since the grid was saved. The favorite-grid JSON is a snapshot
    // and may be days/weeks old, so the bank shape from the live
    // tree is the source of truth for both checks.
    const pathBankIsLiveAndVisible = (path: any[]): boolean => {
      if (path.length !== 4) return false;
      const bank = liveBanks.get(path[3] as string);
      return bank !== undefined && bank.visible !== false;
    };

    if (isMasterAdmin(userLogged, globalOrganizationId)) {
      const survivingPaths = (jsonObject.cascaderSelectMultipleSelectionOptions || []).filter(
        pathBankIsLiveAndVisible,
      );
      const refreshedBanks = survivingPaths
        .map((path: any[]) => liveBanks.get(path[3] as string))
        .filter((bank: any) => bank !== undefined);
      return {
        ...jsonObject,
        organizationSelectMultipleData: refreshedBanks,
        cascaderSelectMultipleSelectionOptions: survivingPaths,
      };
    }

    // A parent-org user's saved grid may reference banks in sub-orgs. Accept any
    // org present in the backend-scoped subtree (cascaderData top-level nodes),
    // not just the currently-focused globalOrganizationId. For a single-org
    // tenant this set is just their own org, so behavior is unchanged.
    const visibleOrgValues = new Set((cascaderData || []).map((org: any) => org.value));
    const filteredPaths = (jsonObject.cascaderSelectMultipleSelectionOptions || []).filter(
      (path: any[]) =>
        path.length > 0 && visibleOrgValues.has(path[0]) && pathBankIsLiveAndVisible(path),
    );
    const filteredBanks = filteredPaths
      .map((path: any[]) => liveBanks.get(path[3] as string))
      .filter((bank: any) => bank !== undefined);

    return {
      ...jsonObject,
      organizationSelectMultipleData: filteredBanks,
      cascaderSelectMultipleSelectionOptions: filteredPaths,
    };
  };

  const onClickFavoriteGrids = (grid: any) => {
    const normalizedGridId = normalizeGridId(grid?.id);
    const rawJsonObject = JSON.parse(grid.grid);
    const jsonObject = filterGridByOrg(rawJsonObject);
    dispatch(updateIdGridSelected(normalizedGridId));
    dispatch(updateTopViewLayout(jsonObject.topViewLayout || []));
    dispatch(updateDetailViewLayout(jsonObject.detailViewLayout || []));
    dispatch(updateStatusViewLayout(jsonObject.statusViewLayout || []));
    dispatch(updateServiceViewLayout(jsonObject.serviceViewLayout || []));
    dispatch(updateOrganizationSelectMultipleData(jsonObject.organizationSelectMultipleData || []));
    dispatch(
      updateCascaderSelectMultipleSelectionOptions(
        jsonObject.cascaderSelectMultipleSelectionOptions || [],
      ),
    );
    setCascaderValue(jsonObject.cascaderSelectMultipleSelectionOptions || []);
    persistSelection(
      jsonObject.organizationSelectMultipleData || [],
      jsonObject.cascaderSelectMultipleSelectionOptions || [],
    );
    // Map view flags
    if (jsonObject.mapViewConfiguration) {
      dispatch(updateFloorsEnabled(jsonObject.mapViewConfiguration.floorsEnabled));
      dispatch(updateNamesEnabled(jsonObject.mapViewConfiguration.namesEnabled));
      dispatch(updateShowOriginal(jsonObject.mapViewConfiguration.showOriginal));
      dispatch(updateServiceModeEnabled(jsonObject.mapViewConfiguration.serviceModeEnabled));
    } else {
      dispatch(updateFloorsEnabled(false));
      dispatch(updateNamesEnabled(false));
      dispatch(updateShowOriginal(false));
      dispatch(updateServiceModeEnabled(false));
    }
  };

  // Track whether the grid load was triggered by an explicit user click
  const userClickedGrid = useRef(false);

  const normalizeGridId = (idGrid: any): string => {
    if (typeof idGrid === "bigint") return idGrid.toString();
    if (typeof idGrid === "string" || typeof idGrid === "number") return String(idGrid);
    return formatUuidT(idGrid);
  };

  const getGrid = async (idGrid: any, isUserClick = false) => {
    userClickedGrid.current = isUserClick;
    const normalizedGridId = normalizeGridId(idGrid);
    if (!normalizedGridId) {
      userClickedGrid.current = false;
      return;
    }
    const token = await GETJwtToken();
    triggerGetGridById({ token, idGrid: normalizedGridId });
  };

  const updateUserPreferredGrid = async (idGrid: string) => {
    if (!userLogged || !userLogged.id || !idGrid) return;
    const previousPrefGridId = userLogged.prefGridId || "";
    const token = await GETJwtToken();
    const orgId =
      (userLogged as any).org_id ||
      (userLogged as any).organization_id ||
      userLogged.organizationId ||
      userLogged.org?.id;
    const body = {
      id: userLogged.id,
      token,
      body: {
        id: userLogged.id,
        role_id: userLogged.roleId,
        org_id: orgId,
        email: userLogged.email,
        first_name: userLogged.firstName,
        middle_name: userLogged.middleName,
        last_name: userLogged.lastName,
        status: userLogged.status,
        command_notifications: userLogged.commandNotifications,
        global_notifications: userLogged.globalNotifications,
        pref_theme: userLogged.prefTheme,
        pref_grid_id: idGrid,
      },
    };
    dispatch(
      updateUserLogged({
        ...userLogged,
        prefGridId: idGrid,
      }),
    );
    try {
      await putUser(body).unwrap();
    } catch {
      dispatch(
        updateUserLogged({
          ...userLogged,
          prefGridId: previousPrefGridId,
        }),
      );
      message.error("Failed to update favorite grid");
    }
  };

  const openFavoriteGridsModal = () => {
    if (!listFavoriteGrids || listFavoriteGrids.length === 0) return;
    setIsGridModalOpen(true);
  };

  const actionIconStyle: React.CSSProperties = {
    fontSize: "20px",
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "10px",
    borderRadius: "6px",
    cursor: "pointer",
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (refreshPage) {
      initData();
    }
  }, [refreshPage]);

  useEffect(() => {
    if (dataGetOrganizationsCascader) {
      buildCascaderArray();
    }
  }, [dataGetOrganizationsCascader]);

  useEffect(() => {
    if (dataGetFavoriteGrids && dataGetFavoriteGrids.data) {
      setListFavoriteGrids(dataGetFavoriteGrids.data);
    }
  }, [dataGetFavoriteGrids]);

  useEffect(() => {
    if (
      userLogged &&
      userLogged.prefGridId &&
      userLogged.prefGridId !== ZERO_UUID_STRING &&
      !(cascaderSelectMultipleSelectionOptions && cascaderSelectMultipleSelectionOptions.length > 0)
    ) {
      getGrid(userLogged.prefGridId);
    }
  }, [userLogged]);

  useEffect(() => {
    if (!dataGridById || !dataGridById.data) return;
    if (cascaderData.length === 0) return;

    // Always apply when user explicitly clicked a saved grid.
    // Only skip auto-load from pref_grid_id when a selection already exists.
    const hasSelection =
      cascaderSelectMultipleSelectionOptions && cascaderSelectMultipleSelectionOptions.length > 0;
    if (userClickedGrid.current || !hasSelection) {
      dispatch(updateFavoriteGridName(dataGridById.data.name || ""));
      onClickFavoriteGrids(dataGridById.data);
    }
    userClickedGrid.current = false;
  }, [dataGridById, cascaderData]);

  // Restore selection from localStorage after cascaderData is built
  useEffect(() => {
    if (cascaderData.length === 0) return;

    // Build the live-bank lookup once: every branch below needs the
    // freshest bank shape, both for first-time localStorage hydration
    // and for "already-in-session" re-sync after the user toggled a
    // bank's `visible` flag in the wizard and navigated back.
    const liveBanks = collectLiveBanks(getVisibleCascaderData(cascaderData));

    // If there's already an active Redux selection, re-sync cascaderValue now that
    // options are available so AntD Cascader can resolve labels (not raw UUIDs).
    //
    // The previous implementation early-returned here without touching
    // `organizationSelectMultipleData`. That made the in-session bug
    // possible: operator selects a bank → toggles `visible` off in the
    // wizard (same SPA session, Redux survives) → navigates back here →
    // cascader refetches → cascaderData has the fresh `visible: false`,
    // but the Redux selection still holds the stale `visible: true`
    // snapshot from the original selection. MonitoringGrid's filter
    // therefore sees the stale flag and renders the hidden bank.
    //
    // Now we ALSO rehydrate the Redux selection from `liveBanks` on
    // every cascaderData refresh — same logic as the localStorage
    // branch below, just sourced from the already-active Redux
    // selection paths. Idempotent when nothing changed.
    if (
      cascaderSelectMultipleSelectionOptions &&
      cascaderSelectMultipleSelectionOptions.length > 0
    ) {
      // Drop selection paths whose banks have since been hidden
      // (visible: false) or deleted. Keeping a path for a hidden bank
      // would render its chip in the cascader header even though the
      // MonitoringGrid filter prevents the bank itself from
      // rendering — confusing UX. `liveBanks` is keyed by id from the
      // freshly fetched `/monitor-view/` response and already
      // reflects the current `visible` flag; missing entries are
      // deleted or hidden banks.
      const survivingPaths = (cascaderSelectMultipleSelectionOptions as any[][]).filter((path) => {
        if (path.length !== 4) return false;
        const bank = liveBanks.get(path[3] as string);
        return bank !== undefined && bank.visible !== false;
      });

      const refreshedBanks = survivingPaths
        .map((path) => liveBanks.get(path[3] as string))
        .filter((bank) => bank !== undefined);

      // Skip dispatch when nothing changed to avoid a redundant store
      // update on every cascaderData refresh.
      //
      // `setCascaderValue` is typed `string[]` upstream but the actual
      // payload is the nested path array `string[][]` that AntD
      // Cascader's `value` prop accepts. Cast at the boundary to match
      // the existing usage in this file (e.g. line above where
      // `cascaderSelectMultipleSelectionOptions` is set the same way)
      // rather than retyping a piece of legacy local state.
      if (survivingPaths.length !== cascaderSelectMultipleSelectionOptions.length) {
        dispatch(updateCascaderSelectMultipleSelectionOptions(survivingPaths));
        setCascaderValue(survivingPaths as unknown as string[]);
      } else {
        setCascaderValue(cascaderSelectMultipleSelectionOptions);
      }
      dispatch(updateOrganizationSelectMultipleData(refreshedBanks));
      return;
    }
    // Skip if user has a pref_grid_id (that path handles its own restore)
    if (userLogged && userLogged.prefGridId && userLogged.prefGridId !== ZERO_UUID_STRING) return;

    try {
      const raw = localStorage.getItem(CASCADER_STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw);
      // Only restore if same user
      if (!userLogged || stored.userId !== userLogged.id) return;

      // `liveBanks` is computed at the top of the effect so both the
      // Redux-already-active branch and this localStorage branch share
      // the same lookup against the freshest `/monitor-view/` response.
      // Also drop paths for banks whose `visible` has since been
      // toggled off — keeping them would render their chip in the
      // cascader header even though MonitoringGrid filters the bank
      // itself out, which is the in-session inconsistency the user
      // reported in this commit chain.
      const validPaths = (stored.cascaderSelectMultipleSelectionOptions || []).filter(
        (path: any[]) => {
          if (path.length !== 4) return false;
          const bank = liveBanks.get(path[3]);
          return bank !== undefined && bank.visible !== false;
        },
      );
      if (validPaths.length === 0) return;

      // Rehydrate selected banks from the live cascader tree rather than
      // trusting the localStorage snapshot. The stored payload carries
      // `visible`/`active`/etc. as they were when the bank was first
      // selected — a later wizard toggle on those fields would not be
      // reflected, so the MonitoringGrid `bank.visible !== false` filter
      // would render a hidden bank. Pulling from `liveBanks` (built from
      // the freshly fetched `/monitor-view/` response) keeps the bank
      // shape in sync with the backend's current state.
      const validBankIdsFromPaths = new Set<string>(validPaths.map((p: any[]) => p[3]));
      const validBanks = Array.from(validBankIdsFromPaths)
        .map((id) => liveBanks.get(id))
        .filter((bank) => bank !== undefined);

      dispatch(updateOrganizationSelectMultipleData(validBanks));
      dispatch(updateCascaderSelectMultipleSelectionOptions(validPaths));
      setCascaderValue(validPaths);
    } catch {
      // Corrupted localStorage — ignore
    }
  }, [cascaderData]);

  useEffect(() => {
    if (clearCascader) {
      onChangeCascader(null, []);
      localStorage.removeItem(CASCADER_STORAGE_KEY);
      dispatch(updateClearCascader(false));
    }
  }, [clearCascader]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__flex">
      {/** ******************************************* */}
      {/** CASCADER */}
      <div className="generalStyles__mlFix">
        <Cascader
          maxTagCount="responsive"
          options={getVisibleCascaderData(cascaderData)}
          onChange={onChangeCascader}
          placeholder="Please select"
          multiple
          value={cascaderValue}
          showCheckedStrategy={SHOW_CHILD}
          style={{ width: "350px" }}
          loading={isLoadingOrganizationCascader || isLoadingFavoriteGrids || isLoadingGridById}
        />
      </div>
      {showSavedGrids && (
        <>
          <Tooltip
            title={
              listFavoriteGrids && listFavoriteGrids.length > 0
                ? "Saved Grids"
                : t("monitoring.noGridsFound")
            }
          >
            <FontAwesomeIcon
              onClick={openFavoriteGridsModal}
              style={actionIconStyle}
              className={`generalStyles__themeIcon1__${theme} organizationCascader__actionIcon`}
              icon={listFavoriteGrids && listFavoriteGrids.length > 0 ? faList : faBan}
            />
          </Tooltip>
          <Modal
            title="Saved Grids"
            open={isGridModalOpen}
            onCancel={() => setIsGridModalOpen(false)}
            footer={null}
            width={720}
          >
            <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "4px" }}>
              {!listFavoriteGrids || listFavoriteGrids.length === 0 ? (
                <Empty description={t("monitoring.noGridsFound")} />
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: "10px",
                  }}
                >
                  {(listFavoriteGrids ?? []).map((grid: any) => {
                    const normalizedGridId = normalizeGridId(grid.id);
                    const isSelectedGrid = normalizedGridId === idGridSelected;
                    const isPreferred =
                      !!userLogged &&
                      !!userLogged.prefGridId &&
                      userLogged.prefGridId === normalizedGridId;

                    return (
                      <div
                        key={normalizedGridId || grid.name}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          updateUserPreferredGrid(normalizedGridId);
                          getGrid(normalizedGridId, true);
                          setIsGridModalOpen(false);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            updateUserPreferredGrid(normalizedGridId);
                            getGrid(normalizedGridId, true);
                            setIsGridModalOpen(false);
                          }
                        }}
                        style={{
                          borderRadius: "8px",
                          padding: "10px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minHeight: "44px",
                          textAlign: "center",
                        }}
                        className={`generalStyles__box__${theme} organizationCascader__gridItem ${
                          isSelectedGrid ? "organizationCascader__gridItem--selected" : ""
                        }`}
                        title={grid.name}
                      >
                        {isPreferred && (
                          <FontAwesomeIcon
                            className={`generalStyles__themeIcon1__${theme} organizationCascader__actionIcon`}
                            icon={faStar}
                          />
                        )}
                        <span
                          className="organizationCascader__gridItemText"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                          }}
                        >
                          {grid.name}
                        </span>
                        {isSelectedGrid && (
                          <FontAwesomeIcon
                            className={`generalStyles__themeIcon1__${theme}`}
                            icon={faCheck}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

export default OrganizationCascaderSelectMultiple;
